import React, { useState, useEffect } from 'react';
import { ClipboardList, Plus, Trash2, Loader2, CheckCircle, AlertCircle, X, Check, Edit2 } from 'lucide-react';
import { logQuestionChange } from '../utils/dataLineage';

const QuestionnaireManagement = () => {
  const dummyQuestions = [
    { id: 1, text: "How does your idea align with the agency's mission goals and strategic priorities? (Covers: Mission Alignment, GIC Fit)", selected: true },
    { id: 2, text: "Does your idea comply with relevant policies or frameworks such as OMB M-24-10, the Technology Reference Model (TRM), or NIST AI RMF 1.0? (Covers: Policy Compliance, TRM Alignment, Risk & Oversight)", selected: true },
    { id: 3, text: "What specific value or impact does this idea deliver — such as cost savings, improved efficiency, reduced risk, or enhanced stakeholder experience? (Covers: Value & Impact, Cost Benefit, User Benefit, Risk Reduction)", selected: true },
    { id: 4, text: "Is the proposed solution technically feasible and are tools, platforms, or data sources already available or reusable to support implementation? (Covers: Feasibility, Readiness, Existing Investments, Dependencies)", selected: true },
    { id: 5, text: "Does this idea introduce innovation, such as AI/ML or automation, and how does it support digital modernization across the enterprise? (Covers: Innovation, Modernization, Scalability, Reusability)", selected: true },
    { id: 6, text: "What are the key risks, constraints, or compliance concerns (e.g., privacy, civil rights, data sensitivity) that should be considered upfront? (Covers: Privacy, Compliance, DHS Policies, FedRAMP, Technical Limitations)", selected: true },
    { id: 7, text: "Are required resources—including staffing, budget, and timelines—identified and allocated to support delivery feasibility? (Covers: Resource Allocation, Timeline Planning, Cost Awareness)", selected: true },
    { id: 8, text: "Has the technical architecture been designed, including system integration points, infrastructure needs, and technology stack alignment? (Covers: Technical Architecture, Integration, Infrastructure Readiness)", selected: true },
    { id: 9, text: "Have key stakeholders been identified, and is there a plan for their engagement across all stages of the project lifecycle? (Covers: Stakeholder Engagement, Communication Plan, Governance Visibility)", selected: true },
    { id: 10, text: "Are there defined quality assurance, risk mitigation, documentation standards, and compliance checks to guide delivery and ensure accountability? (Covers: Quality Assurance, Documentation, Risk Mitigation, Compliance Verification, Performance Metrics)", selected: true }
  ];

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState(null);
  const [newQuestion, setNewQuestion] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [editQuestion, setEditQuestion] = useState(null);
  const [editQuestionText, setEditQuestionText] = useState('');
  const [usingDummyData, setUsingDummyData] = useState(false);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/question/get_questions');

      if (!response.ok) {
        throw new Error('Failed to fetch questions from API. Using dummy data instead.');
      }

      const data = await response.json();
      const formattedQuestions = data.map(q => ({
        id: q.id || q.question_id,
        text: q.Question,
        selected: false
      }));
      setQuestions(formattedQuestions);
      setUsingDummyData(false);
    } catch (err) {
      console.error('Error fetching questions:', err);
      setQuestions(dummyQuestions);
      setUsingDummyData(true);
    } finally {
      setLoading(false);
    }
  };

  const toggleQuestion = (id) => {
    setQuestions(questions.map(q =>
      q.id === id ? { ...q, selected: !q.selected } : q
    ));
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    if (!newQuestion.trim()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      if (usingDummyData) {
        const newId = Math.max(...questions.map(q => q.id), 0) + 1;
        setQuestions([...questions, { id: newId, text: newQuestion, selected: true }]);
      } else {
        const response = await fetch('/api/question/add_question', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ question: newQuestion })
        });

        if (!response.ok) throw new Error('Failed to add question');
        await fetchQuestions();

        // Log question creation
        const newId = questions.length > 0 ? Math.max(...questions.map(q => q.id)) + 1 : 1;
        await logQuestionChange(newId, newQuestion, 'CREATE', 'question_created', null, newQuestion);
      }

      setSuccessMessage('Question added successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowAddModal(false);
      setNewQuestion('');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteQuestion = async () => {
    if (!selectedQuestion) return;

    setIsSubmitting(true);
    setError(null);

    try {
      if (usingDummyData) {
        setQuestions(questions.filter(q => q.id !== selectedQuestion.id));
      } else {
        const response = await fetch(`/api/question/delete_question?id=${selectedQuestion.id}`, {
          method: 'DELETE'
        });

        if (!response.ok) throw new Error('Failed to delete question');
        setQuestions(questions.filter(q => q.id !== selectedQuestion.id));

        // Log question deletion
        await logQuestionChange(selectedQuestion.id, selectedQuestion.text, 'DELETE', 'question_deleted', selectedQuestion.text, null);
      }

      setSuccessMessage('Question deleted successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowDeleteModal(false);
      setSelectedQuestion(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectAll = () => {
    setQuestions(questions.map(q => ({ ...q, selected: true })));
  };

  const deselectAll = () => {
    setQuestions(questions.map(q => ({ ...q, selected: false })));
  };

  const openEditModal = (question) => {
    setEditQuestion(question);
    setEditQuestionText(question.text);
    setShowEditModal(true);
    setError(null);
  };

  const handleEditQuestion = async (e) => {
    e.preventDefault();
    if (!editQuestionText.trim() || !editQuestion) return;

    setIsSubmitting(true);
    setError(null);

    try {
      if (usingDummyData) {
        setQuestions(questions.map(q =>
          q.id === editQuestion.id ? { ...q, text: editQuestionText } : q
        ));
      } else {
        const response = await fetch(`/api/question/update_question`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: editQuestion.id,
            question: editQuestionText
          })
        });

        if (!response.ok) throw new Error('Failed to update question');

        setQuestions(questions.map(q =>
          q.id === editQuestion.id ? { ...q, text: editQuestionText } : q
        ));

        // Log question update
        if (editQuestion.text !== editQuestionText) {
          await logQuestionChange(editQuestion.id, editQuestionText, 'UPDATE', 'question_text', editQuestion.text, editQuestionText);
        }
      }

      setSuccessMessage('Question updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowEditModal(false);
      setEditQuestion(null);
      setEditQuestionText('');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDeleteModal = (question) => {
    setSelectedQuestion(question);
    setShowDeleteModal(true);
    setError(null);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Loader2 style={{ width: '48px', height: '48px', color: 'var(--cares-purple)', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  const selectedCount = questions.filter(q => q.selected).length;

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Page Header - Title removed to avoid duplication with Dashboard Header */}
      {usingDummyData && (
        <div style={{
          marginBottom: '24px',
          background: 'color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card))',
          border: '1px solid var(--cares-amber)',
          borderRadius: '8px',
          padding: '10px 14px',
          fontSize: '13px',
          color: 'var(--cares-amber)'
        }}>
          Using dummy data as the API is not available
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div style={{
          background: 'color-mix(in srgb, var(--cares-green) 22%, var(--cares-surface-card))',
          border: '1px solid var(--cares-green)',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <CheckCircle style={{ width: '20px', height: '20px', color: 'var(--cares-green)' }} />
          <span style={{ color: 'var(--cares-green)', fontSize: '14px', fontWeight: '500' }}>{successMessage}</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          background: 'color-mix(in srgb, var(--cares-orange) 22%, var(--cares-surface-card))',
          border: '1px solid var(--cares-orange)',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <AlertCircle style={{ width: '20px', height: '20px', color: 'var(--cares-orange)' }} />
          <span style={{ color: 'var(--cares-orange)', fontSize: '14px', fontWeight: '500' }}>{error}</span>
        </div>
      )}

      {/* Action Bar */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div style={{ fontSize: '14px', color: 'var(--cares-text-label)' }}>
          Selected: <span style={{ fontWeight: '600', color: 'var(--cares-purple)' }}>{selectedCount}</span> of <span style={{ fontWeight: '600', color: 'var(--cares-text-1)' }}>{questions.length}</span>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={selectAll}
            style={{
              background: 'var(--cares-surface-card)',
              color: 'var(--cares-text-label)',
              border: '1px solid var(--cares-line-strong)',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => { e.currentTarget.style.background = 'var(--cares-surface-board)'; }}
            onMouseOut={(e) => { e.currentTarget.style.background = 'var(--cares-surface-card)'; }}
          >
            Select All
          </button>
          <button
            onClick={deselectAll}
            style={{
              background: 'var(--cares-surface-card)',
              color: 'var(--cares-text-label)',
              border: '1px solid var(--cares-line-strong)',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => { e.currentTarget.style.background = 'var(--cares-surface-board)'; }}
            onMouseOut={(e) => { e.currentTarget.style.background = 'var(--cares-surface-card)'; }}
          >
            Deselect All
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
              color: 'var(--cares-text-max)',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 2px 4px rgba(139, 92, 246, 0.3)',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <Plus style={{ width: '16px', height: '16px' }} />
            Add Question
          </button>
        </div>
      </div>

      {/* Questions List */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {questions.map((question, index) => (
          <div
            key={question.id}
            style={{
              padding: '16px 20px',
              borderBottom: index < questions.length - 1 ? '1px solid var(--cares-line-card)' : 'none',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '16px',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = 'var(--cares-surface-board)'}
            onMouseOut={(e) => e.currentTarget.style.background = 'var(--cares-surface-card)'}
          >
            <div style={{ display: 'flex', alignItems: 'center', paddingTop: '2px' }}>
              <input
                type="checkbox"
                checked={question.selected}
                onChange={() => toggleQuestion(question.id)}
                style={{
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer',
                  accentColor: '#8b5cf6'
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: '12px'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: 'var(--cares-text-label)',
                    marginBottom: '6px'
                  }}>
                    Question {index + 1}
                  </div>
                  <p style={{
                    fontSize: '14px',
                    color: 'var(--cares-text-1)',
                    lineHeight: '1.6',
                    margin: 0
                  }}>
                    {question.text}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <button
                    onClick={() => openEditModal(question)}
                    style={{
                      background: 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))',
                      color: 'var(--cares-blue)',
                      border: '1px solid var(--cares-blue)',
                      borderRadius: '6px',
                      padding: '6px 10px',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => { e.currentTarget.style.background = 'var(--cares-blue)'; e.currentTarget.style.color = 'var(--cares-text-max)'; }}
                    onMouseOut={(e) => { e.currentTarget.style.background = 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))'; e.currentTarget.style.color = 'var(--cares-blue)'; }}
                  >
                    <Edit2 style={{ width: '14px', height: '14px' }} />
                    Edit
                  </button>
                  <button
                    onClick={() => openDeleteModal(question)}
                    style={{
                      background: 'color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))',
                      color: 'var(--cares-orange)',
                      border: '1px solid var(--cares-orange)',
                      borderRadius: '6px',
                      padding: '6px 10px',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => { e.currentTarget.style.background = 'var(--cares-orange)'; e.currentTarget.style.color = 'var(--cares-text-max)'; }}
                    onMouseOut={(e) => { e.currentTarget.style.background = 'color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))'; e.currentTarget.style.color = 'var(--cares-orange)'; }}
                  >
                    <Trash2 style={{ width: '14px', height: '14px' }} />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Question Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--cares-surface-card)',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '600px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid var(--cares-line-card)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--cares-text-1)', margin: 0 }}>Add New Question</h2>
              <button
                onClick={() => setShowAddModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: 'var(--cares-text-label)'
                }}
              >
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>
            <form onSubmit={handleAddQuestion} style={{ padding: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-3)', marginBottom: '8px' }}>
                  Question Text *
                </label>
                <textarea
                  value={newQuestion}
                  onChange={(e) => setNewQuestion(e.target.value)}
                  required
                  rows="4"
                  placeholder="Enter the question text..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--cares-line-strong)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
              <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  style={{
                    background: 'var(--cares-surface-card)',
                    color: 'var(--cares-text-label)',
                    border: '1px solid var(--cares-line-strong)',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    background: isSubmitting ? 'var(--cares-surface-raised)' : 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
                    color: 'var(--cares-text-max)',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {isSubmitting ? <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} /> : null}
                  {isSubmitting ? 'Adding...' : 'Add Question'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Question Modal */}
      {showEditModal && editQuestion && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--cares-surface-card)',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '600px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid var(--cares-line-card)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--cares-text-1)', margin: 0 }}>Edit Question</h2>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: 'var(--cares-text-label)'
                }}
              >
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>
            <form onSubmit={handleEditQuestion} style={{ padding: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-3)', marginBottom: '8px' }}>
                  Question Text *
                </label>
                <textarea
                  value={editQuestionText}
                  onChange={(e) => setEditQuestionText(e.target.value)}
                  required
                  rows="4"
                  placeholder="Enter the question text..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--cares-line-strong)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
              <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  style={{
                    background: 'var(--cares-surface-card)',
                    color: 'var(--cares-text-label)',
                    border: '1px solid var(--cares-line-strong)',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    background: isSubmitting ? 'var(--cares-surface-raised)' : 'linear-gradient(135deg, var(--cares-blue) 0%, var(--cares-blue) 100%)',
                    color: 'var(--cares-text-max)',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {isSubmitting ? <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} /> : null}
                  {isSubmitting ? 'Updating...' : 'Update Question'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedQuestion && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--cares-surface-card)',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '450px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid var(--cares-line-card)'
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--cares-text-1)', margin: 0 }}>Delete Question</h2>
            </div>
            <div style={{ padding: '24px' }}>
              <p style={{ fontSize: '14px', color: 'var(--cares-text-label)', margin: 0, lineHeight: '1.6' }}>
                Are you sure you want to delete this question? This action cannot be undone.
              </p>
            </div>
            <div style={{
              padding: '16px 24px',
              background: 'var(--cares-surface-board)',
              borderTop: '1px solid var(--cares-line-card)',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={isSubmitting}
                style={{
                  background: 'var(--cares-surface-card)',
                  color: 'var(--cares-text-label)',
                  border: '1px solid var(--cares-line-strong)',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteQuestion}
                disabled={isSubmitting}
                style={{
                  background: isSubmitting ? 'var(--cares-surface-raised)' : 'var(--cares-orange)',
                  color: 'var(--cares-text-max)',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {isSubmitting ? <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} /> : null}
                {isSubmitting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionnaireManagement;
